<template>
  <div>
    <b-card>
      <div class="d-flex flex-wrap align-items-start justify-content-between mb-2">
        <TitleTable titleTable="GLP-1 Appointments" iconTable="fas fa-calendar-alt" />
        <b-button
          variant="outline-success"
          size="sm"
          class="mt-2 mt-md-0"
          :disabled="loading || exporting"
          @click="exportCsv">
          <i class="fas fa-file-csv mr-1"></i>
          {{ exporting ? 'Exporting…' : 'Export CSV' }}
        </b-button>
      </div>
      <b-row class="wl-filters m-1 mb-1 align-items-start">
        <b-col cols="12" md="3">
          <b-form-group label="Facility" label-class="font-weight-bold mb-1" class="mb-md-0">
            <treeselect
              v-model="filters.facilities_id"
              :options="facilityOptions"
              :normalizer="normalizer"
              :multiple="true"
              :clearable="true"
              placeholder="All facilities"
              :disabled="facilityOptionsLoading" />
          </b-form-group>
        </b-col>
        <b-col cols="12" md="3">
          <b-form-group label="OMA" label-class="font-weight-bold mb-1" class="mb-md-0">
            <treeselect
              v-model="filters.oma_id"
              :options="omaOptions"
              :normalizer="omaNormalizer"
              :clearable="true"
              placeholder="All OMAs"
              :disabled="omaOptionsLoading" />
          </b-form-group>
        </b-col>
        <b-col cols="12" md="3" class="d-flex justify-content-end align-items-end pb-1">
          <b-button
            variant="outline-primary"
            class="wl-filter-btn mt-2 mt-md-0"
            :disabled="facilityOptionsLoading || omaOptionsLoading || loading"
            @click="load(1)">
            Filter
          </b-button>
        </b-col>
      </b-row>
      <b-alert v-if="filters.oma_id" show variant="info" class="mx-1 mb-3 py-2">
        Filtering by OMA excludes scheduled appointments without an office visit yet.
      </b-alert>

      <b-table
        :items="items"
        :fields="fields"
        bordered
        responsive
        show-empty
        empty-text="No appointments found">
        <template #cell(visit_date)="data">
          {{ formatDate(data.value) }}
        </template>
        <template #cell(patient)="data">
          <div class="font-weight-bold">{{ patientName(data.item) }}</div>
        </template>
        <template #cell(status)="data">
          {{ data.value || '—' }}
        </template>
        <template #cell(oma)="data">
          <span :class="{ 'text-muted': !omaName(data.item) || omaName(data.item) === '—' }">
            {{ omaName(data.item) }}
          </span>
        </template>
        <template #cell(office_visit_id)="data">
          <span v-if="data.value">#{{ data.value }}</span>
          <span v-else class="text-muted">—</span>
        </template>
        <template #cell(actions)="data">
          <b-button size="sm" variant="outline-primary" @click="openDetail(data.item)">
            <i class="fas fa-eye"></i>
          </b-button>
        </template>
      </b-table>

      <PaginationTable
        :dataMeta="dataMeta"
        :totalUsers="totalUsers"
        :perPage="perPage"
        :currentPage="{ page: currentPage }"
        @page-changed="load" />
    </b-card>

    <b-modal
      v-model="detailVisible"
      size="xl"
      centered
      title="Appointment detail"
      ok-only
      ok-title="Close"
      @hidden="detail = null">
      <div v-if="detailLoading" class="text-center py-4 text-muted">Loading…</div>
      <div v-else-if="detail">
        <b-row class="mb-3">
          <b-col cols="12" md="4">
            <small class="text-muted d-block">Patient</small>
            <strong>{{ patientName(detail) }}</strong>
          </b-col>
          <b-col cols="12" md="4">
            <small class="text-muted d-block">Visit date</small>
            <strong>{{ formatDate(detail.visit_date) }}</strong>
          </b-col>
          <b-col cols="12" md="4">
            <small class="text-muted d-block">Status</small>
            <strong>{{ detail.status || '—' }}</strong>
          </b-col>
          <b-col cols="12" md="4" class="mt-2">
            <small class="text-muted d-block">Pay status</small>
            <strong>{{ (detail.pay_status && detail.pay_status.name) || '—' }}</strong>
          </b-col>
          <b-col cols="12" md="4" class="mt-2">
            <small class="text-muted d-block">Treatment</small>
            <strong>{{ treatmentName(detail) }}</strong>
          </b-col>
          <b-col cols="12" md="4" class="mt-2">
            <small class="text-muted d-block">Facility</small>
            <strong>{{ facilityName(detail) }}</strong>
          </b-col>
          <b-col cols="12" md="4" class="mt-2">
            <small class="text-muted d-block">OMA</small>
            <strong>{{ omaName(detail) }}</strong>
          </b-col>
        </b-row>
        <hr />
        <WlOfficeVisitInherited :office-visit="detail.office_visit || null" />
      </div>
    </b-modal>

    <ToastNotification ref="toast" />
  </div>
</template>

<script>
import {
  BCard,
  BRow,
  BCol,
  BTable,
  BButton,
  BFormGroup,
  BModal,
  BAlert,
} from 'bootstrap-vue'
import Treeselect from '@riophae/vue-treeselect'
import TitleTable from '@/components/TitleTable.vue'
import PaginationTable from '@/components/PaginationTable'
import ToastNotification from '@/components/ToastNotification'
import WlOfficeVisitInherited from './components/WlOfficeVisitInherited.vue'
import wlAdminMixin from '../mixins/wlAdminMixin'
import wlLaneMixin from '../mixins/wlLaneMixin'
import { exportRowsToCsv, fetchAllPaginatedPages, csvFilename } from '../utils/exportCsv'
import axiosF from '@/core/services/api/admin/facilities'
import axiosU from '@/core/services/api/admin/user'

export default {
  name: 'TableWlAppointments',
  mixins: [wlAdminMixin, wlLaneMixin],
  components: {
    BCard,
    BRow,
    BCol,
    BTable,
    BButton,
    BFormGroup,
    BModal,
    BAlert,
    Treeselect,
    TitleTable,
    PaginationTable,
    ToastNotification,
    WlOfficeVisitInherited,
  },
  data() {
    return {
      normalizer(node) {
        return { id: node.id, label: node.name }
      },
      omaNormalizer(node) {
        return { id: node.id, label: node.label || node.name }
      },
      fields: [
        { key: 'id', label: 'ID' },
        { key: 'visit_date', label: 'Date' },
        { key: 'patient', label: 'Patient' },
        { key: 'status', label: 'Status' },
        { key: 'oma', label: 'OMA' },
        { key: 'office_visit_id', label: 'Office visit' },
        { key: 'actions', label: '' },
      ],
      filters: {
        // All facilities by default (omit facilities_id)
        facilities_id: [],
        oma_id: null,
      },
      facilityOptions: [],
      facilityOptionsLoading: false,
      omaOptions: [],
      omaOptionsLoading: false,
      detailVisible: false,
      detailLoading: false,
      detail: null,
      exporting: false,
    }
  },
  created() {
    this.loadFacilities()
    this.loadOmas()
    this.load(1)
  },
  methods: {
    buildListParams() {
      return this.buildListQueryParams({
        facilities_id: this.filters.facilities_id,
        oma_id: this.filters.oma_id,
      })
    },
    async loadFacilities() {
      this.facilityOptionsLoading = true
      try {
        const { data } = await axiosF.facilityList(1000)
        this.facilityOptions = (data || []).map(item => ({
          id: item.id,
          name: item.name,
        }))
      } catch {
        this.facilityOptions = []
      } finally {
        this.facilityOptionsLoading = false
      }
    },
    async loadOmas() {
      this.omaOptionsLoading = true
      try {
        const res = await axiosU.userFilter(1000, {
          role: 3,
          status: true,
        })
        const list = (res && res.registro && res.registro.data) || []
        this.omaOptions = list.map(u => ({
          id: u.id,
          label: `${u.name || ''} ${u.lastname || ''}`.trim() || `#${u.id}`,
        }))
      } catch {
        this.omaOptions = []
      } finally {
        this.omaOptionsLoading = false
      }
    },
    async load(page = 1) {
      this.loading = true
      try {
        const res = await this.wlAppointmentsApi.listPaginated(
          this.perPage,
          this.buildListParams(),
          page
        )
        this.applyPagination(res)
      } catch (e) {
        this.items = []
        if (this.$refs.toast) {
          this.$refs.toast.danger(e.message || 'Could not load appointments')
        }
      } finally {
        this.loading = false
      }
    },
    async openDetail(item) {
      this.detailVisible = true
      this.detailLoading = true
      this.detail = null
      try {
        this.detail = await this.wlAppointmentsApi.getById(item.id)
      } catch (e) {
        this.$refs.toast.danger(e.message || 'Could not load appointment detail')
        this.detailVisible = false
      } finally {
        this.detailLoading = false
      }
    },
    async exportCsv() {
      this.exporting = true
      try {
        const rows = await fetchAllPaginatedPages((perPage, page) =>
          this.wlAppointmentsApi.listPaginated(perPage, this.buildListParams(), page)
        )
        if (!rows.length) {
          if (this.$refs.toast) this.$refs.toast.warning('No appointments to export')
          return
        }
        exportRowsToCsv(
          rows,
          [
            { key: 'id', label: 'ID' },
            {
              key: 'visit_date',
              label: 'Date',
              value: row => this.formatDate(row.visit_date),
            },
            { key: 'patient', label: 'Patient', value: row => this.patientName(row) },
            { key: 'status', label: 'Status', value: row => row.status || '' },
            { key: 'oma', label: 'OMA', value: row => this.omaName(row) },
            {
              key: 'office_visit_id',
              label: 'Office visit ID',
              value: row => row.office_visit_id || '',
            },
            {
              key: 'facility',
              label: 'Facility',
              value: row => this.facilityName(row),
            },
            {
              key: 'treatment',
              label: 'Treatment',
              value: row => this.treatmentName(row),
            },
          ],
          csvFilename('wl_appointments')
        )
      } catch (e) {
        if (this.$refs.toast) {
          this.$refs.toast.danger((e && e.message) || 'Could not export CSV')
        }
      } finally {
        this.exporting = false
      }
    },
    formatDate(value) {
      if (!value) return '—'
      try {
        return new Date(value).toLocaleString()
      } catch {
        return value
      }
    },
    patientName(item) {
      if (!item) return '—'
      const p = item.patient || item.weightloss_patient || {}
      const first = p.name || p.first_name || ''
      const last = p.lastname || p.last_name || ''
      const full = `${first} ${last}`.trim()
      return full || (item.weightloss_patient_id ? `#${item.weightloss_patient_id}` : '—')
    },
    omaName(item) {
      const ov = item && item.office_visit
      if (!ov) return '—'
      const o = ov.omas
      if (!o) return '—'
      const full = `${o.name || ''} ${o.lastname || ''}`.trim()
      return full || (ov.omas_id ? `#${ov.omas_id}` : '—')
    },
    treatmentName(item) {
      const wp = item && item.weightloss_patient
      const t = (wp && wp.treatment) || (item && item.treatment)
      return (t && t.name) || '—'
    },
    facilityName(item) {
      const f = item && item.facility
      return (f && f.name) || '—'
    },
  },
}
</script>

<style scoped>
.wl-filter-btn {
  min-width: 7rem;
}
</style>
